import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import {
  TerminalSquare,
  ChevronUp,
  Trash2,
  Search,
  X,
  WrapText,
  Clock,
  Copy,
  Check,
  ArrowDownToLine,
  Braces,
} from "lucide-react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { showToast } from "utils/toast";
import { useTranslation } from "react-i18next";
import {
  parseSerialLine,
  classifyEvent,
  describeEvent,
} from "utils/serialEvents";
import {
  gStr,
  IMPACT_FS_G,
  severityForG,
  ImpactMeter,
  ImpactPulse,
  AxisBars,
  TX_TITLE,
  eventDetailRows,
  SnippetSparkline,
} from "./impactVisuals";
import { dateLocale } from "i18n";
import { highlight } from "./highlight";

// Cap on rendered rows. Search filters the full (uncleared) buffer, but we only
// paint the last N to keep a fast streaming console.
const DISPLAY_CAP = 1500;

// Colour each line by its event family, so a busy log is scannable at a glance.
const LINE_COLOR = {
  cmd: "#F2A65A", // [host] > outgoing command
  error: "#F87171", // nack / error / save_failed / fault
  warn: "#FBBF24", // warning
  ok: "#4ADE80", // ack
  impact: "#F472B6", // live impact detection
  session: "#C4B5FD", // session lifecycle
  sync: "#67E8F9", // sync (config / time / erase / offload)
  tx: "#2DD4BF", // radio transmit (impact / snippet / status)
  button: "#FDE68A", // physical button press
  store: "#86EFAC", // store saved
  ble: "#7DD3FC", // ble events / [ble] prose
  host: "#94A3B8", // [host] prose
  meta: "#94A3B8", // status / led / id
  json: "#A5B4FC", // other NDJSON
  default: "#E2E8F0", // device prose
};

const fmtTime = (ts) =>
  new Date(ts).toLocaleTimeString(dateLocale("en-US"), { hour12: false });

// Firmware per-packet debug echo (`[cast] --- impact … ---`, the hex dump, and
// its `decoded: …` line). It's redundant with the structured `tx` events we
// render nicely, so the decoded view hides it; raw view shows everything.
const isDeviceDump = (s) => /^\s*\[cast\]/.test(s);

// Rich detail card: a titled breakdown of one decoded event, with a big
// sparkline for load snippets and the exact raw payload.
const EventTooltip = ({ obj, raw }) => {
  const { t } = useTranslation();
  const color = LINE_COLOR[classifyEvent(obj, raw)] || "#E2E8F0";
  const evType = obj.t;
  const title =
    evType === "tx"
      ? TX_TITLE[obj.pkt] || t("devices.terminal.transmit")
      : evType.charAt(0).toUpperCase() + evType.slice(1);
  const subtitle =
    evType === "tx"
      ? `t:tx · ${obj.pkt}`
      : `t:${evType}${obj.ev ? ` · ${obj.ev}` : ""}`;
  const rows = eventDetailRows(obj);
  const isSnippet =
    evType === "tx" && obj.pkt === "0x11" && Array.isArray(obj.samples_mg);
  const saturated = isSnippet && obj.samples_mg.some((s) => s >= 10000);
  const isImpact = evType === "impact" && typeof obj.peak_mg === "number";
  const isImpactTx =
    evType === "tx" &&
    obj.pkt === "0x10" &&
    [obj.x_mg, obj.y_mg, obj.z_mg].some((c) => typeof c === "number");
  const sev = isImpact ? severityForG(obj.peak_mg / 1000) : null;
  const txSev = isImpactTx ? severityForG((obj.mag_mg || 0) / 1000) : null;

  const copyJson = () => {
    navigator.clipboard?.writeText(raw).then(
      () => showToast.success(t("devices.impactVisual.copiedToClipboard")),
      () => showToast.error(t("devices.impactVisual.copyFailed"))
    );
  };

  return (
    <div className="w-[330px] rounded-xl border border-[#242A38] bg-[#12141C] p-3 shadow-2xl">
      <div className="mb-2 flex items-center gap-2">
        <span
          className="h-2 w-2 flex-shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="text-[12px] font-semibold text-[#F3F4F6]">
          {title}
        </span>
        <span className="ml-1 font-mono text-[10px] text-[#6B7280]">
          {subtitle}
        </span>
        <button
          onClick={copyJson}
          title={t("devices.impactVisual.copyJson")}
          aria-label={t("devices.impactVisual.copyJson")}
          className="ml-auto flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-[#7A8394] transition duration-150 hover:bg-[#20242F] hover:text-[#E5E7EB]"
        >
          <Copy size={12} />
        </button>
      </div>

      {isSnippet && (
        <div className="mb-2 rounded-lg bg-[#0C0E14] p-2">
          <div className="mb-1 flex justify-between font-mono text-[9px]">
            <span className="text-[#5B6472]">
              {t("devices.terminal.peakValue", { value: gStr(obj.max_mg) })}
            </span>
            {saturated ? (
              <span className="font-semibold text-[#F87171]">
                {t("devices.impactVisual.offScale")}
              </span>
            ) : (
              <span className="text-[#5B6472]">0–10 g</span>
            )}
          </div>
          <SnippetSparkline
            samples={obj.samples_mg}
            w={302}
            h={72}
            interactive
          />
          <div className="mt-1 flex justify-between font-mono text-[9px] text-[#5B6472]">
            <span>0 s</span>
            <span>
              <span className="text-[#9CA3AF]/70">
                {t("devices.impactVisual.gravity")}
              </span>
              {" · "}
              <span className="text-[#E07A3A]/80">
                {t("devices.impactVisual.trigger")}
              </span>
            </span>
            <span>1.9 s</span>
          </div>
        </div>
      )}

      {isImpact && (
        <div className="mb-2 rounded-lg bg-[#0C0E14] p-2">
          <div className="mb-1.5 flex items-baseline justify-between">
            <span
              className="font-mono text-[16px] font-semibold leading-none"
              style={{ color: sev.color }}
            >
              {gStr(obj.peak_mg)}
            </span>
            <span
              className="text-[10px] font-semibold uppercase tracking-wide"
              style={{ color: sev.color }}
            >
              {sev.label}
              {obj.band && obj.band !== sev.level ? ` · ${obj.band}` : ""}
            </span>
          </div>
          <ImpactMeter peakMg={obj.peak_mg} w={302} h={16} />
          <div className="mt-0.5 flex justify-between font-mono text-[9px] text-[#5B6472]">
            <span>0 g</span>
            <span>{t("devices.impactVisual.severityScale")}</span>
            <span>{IMPACT_FS_G} g+</span>
          </div>
          {(obj.rise_ms != null || obj.dur_ms != null) && (
            <div className="mt-1.5 border-t border-[#161A24] pt-1">
              <ImpactPulse
                riseMs={obj.rise_ms}
                durMs={obj.dur_ms}
                color={sev.color}
              />
            </div>
          )}
        </div>
      )}

      {isImpactTx && (
        <div className="mb-2 rounded-lg bg-[#0C0E14] p-2">
          <div className="mb-1.5 flex items-baseline justify-between">
            <span
              className="font-mono text-[16px] font-semibold leading-none"
              style={{ color: txSev.color }}
            >
              {gStr(obj.mag_mg)}
            </span>
            <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">
              {t("devices.terminal.magnitude")}
            </span>
          </div>
          <AxisBars
            comps={[obj.x_mg, obj.y_mg, obj.z_mg]}
            w={302}
            h={54}
            labels
          />
          <p className="mt-0.5 text-center font-mono text-[9px] text-[#5B6472]">
            {t("devices.terminal.perAxis")}
          </p>
        </div>
      )}

      <div className="divide-y divide-[#1C2130]">
        {rows.map(([label, value]) => (
          <div
            key={label}
            className="flex items-baseline justify-between gap-4 py-1"
          >
            <span className="text-[10px] uppercase tracking-wide text-[#6B7280]">
              {label}
            </span>
            <span className="text-right font-mono text-[11px] text-[#E5E7EB]">
              {value}
            </span>
          </div>
        ))}
      </div>

      {obj.raw && (
        <div className="mt-2 border-t border-[#1C2130] pt-2">
          <p className="mb-0.5 text-[9px] uppercase tracking-wide text-[#6B7280]">
            {t("devices.terminal.rawBytes", {
              count: Math.floor(String(obj.raw).length / 2),
            })}
          </p>
          <p className="break-all font-mono text-[10px] leading-[15px] text-[#7A8394]">
            {obj.raw}
          </p>
        </div>
      )}
    </div>
  );
};

const IconBtn = ({ active, title, onClick, danger, children }) => (
  <button
    onClick={onClick}
    title={title}
    aria-label={title}
    className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md transition duration-150 ${
      active
        ? "bg-[#2A2F3D] text-[#F3F4F6]"
        : `text-[#7A8394] hover:bg-[#20242F] ${
            danger ? "hover:text-[#F87171]" : "hover:text-[#E5E7EB]"
          }`
    }`}
  >
    {children}
  </button>
);

const SerialTerminal = ({
  receivedData = [],
  connected = false,
  className = "",
  docked = false,
  terminalHeight = 160,
  collapsed = false,
  onToggleCollapse = null,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [clearedAtIndex, setClearedAtIndex] = useState(0);
  const [query, setQuery] = useState("");
  const [wrap, setWrap] = useState(true);
  const [showTs, setShowTs] = useState(true);
  const [decode, setDecode] = useState(true);
  const [atBottom, setAtBottom] = useState(true);
  const [unseen, setUnseen] = useState(0);
  const [copied, setCopied] = useState(false);
  const [selected, setSelected] = useState(null); // { id, obj, raw, rect }

  const scrollRef = useRef(null);
  const prevLenRef = useRef(0);

  // Click a line to pin its detail card; click the same line (or anywhere
  // outside the card) to dismiss.
  const openLine = useCallback((id, obj, raw, rect) => {
    setSelected((prev) => (prev?.id === id ? null : { id, obj, raw, rect }));
  }, []);
  const closeLine = useCallback(() => setSelected(null), []);

  // Dismiss on an outside click or Escape. Row and card mousedowns
  // stopPropagation, so this only fires for clicks elsewhere.
  useEffect(() => {
    if (!selected) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeLine();
    };
    document.addEventListener("mousedown", closeLine);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", closeLine);
      document.removeEventListener("keydown", onKey);
    };
  }, [selected, closeLine]);

  const open = docked ? terminalHeight > 0 : isExpanded;

  // ── Data: search the whole uncleared buffer, render the last DISPLAY_CAP ──
  const base = useMemo(
    () => receivedData.slice(clearedAtIndex),
    [receivedData, clearedAtIndex]
  );
  // Decoded view drops the firmware's redundant `[cast]` packet dumps; raw view
  // keeps everything.
  const visible = useMemo(
    () => (decode ? base.filter((e) => !isDeviceDump(String(e.data))) : base),
    [base, decode]
  );
  const total = visible.length;

  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return visible;
    return visible.filter((e) => String(e.data).toLowerCase().includes(q));
  }, [visible, q]);

  const matchCount = q ? matches.length : null;
  const isTruncated = matches.length > DISPLAY_CAP;
  const displayData = isTruncated ? matches.slice(-DISPLAY_CAP) : matches;

  const scrollToBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    setUnseen(0);
    setAtBottom(true);
  }, []);

  // Track whether we're pinned to the bottom (so streaming doesn't yank the
  // view while you're reading history).
  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const bottom = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
    setAtBottom(bottom);
    if (bottom) setUnseen(0);
  }, []);

  // New lines: follow if pinned to bottom, else count how many you haven't seen.
  useEffect(() => {
    if (!open) {
      prevLenRef.current = displayData.length;
      return;
    }
    const grew = displayData.length - prevLenRef.current;
    if (grew > 0) {
      if (atBottom) requestAnimationFrame(scrollToBottom);
      else setUnseen((n) => n + grew);
    }
    prevLenRef.current = displayData.length;
  }, [displayData.length, open, atBottom, scrollToBottom]);

  // Jump to newest when opening or when the query changes (see latest matches).
  useEffect(() => {
    if (open) requestAnimationFrame(scrollToBottom);
    // eslint-disable-next-line
  }, [open, q]);

  const handleClear = useCallback(
    (e) => {
      e?.stopPropagation?.();
      setClearedAtIndex(receivedData.length);
      setUnseen(0);
    },
    [receivedData.length]
  );

  const handleCopy = useCallback(
    (e) => {
      e?.stopPropagation?.();
      const text = displayData
        .map((l) => (showTs ? `${fmtTime(l.timestamp)} ${l.data}` : l.data))
        .join("\n");
      navigator.clipboard?.writeText(text).then(
        () => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1200);
        },
        () => {}
      );
    },
    [displayData, showTs]
  );

  const stop = (e) => e.stopPropagation();

  // Rows are memoised so hovering (which sets tooltip state) doesn't re-render
  // the whole log during a mouse sweep.
  const renderedRows = useMemo(
    () =>
      displayData.map((entry, i) => {
        const raw = String(entry.data);
        const obj = parseSerialLine(raw);
        const color = LINE_COLOR[classifyEvent(obj, raw)];
        const summary = decode && obj ? describeEvent(obj) : null;
        const shown = summary != null ? summary : raw;
        const rowId = `${entry.timestamp}-${i}`;
        return (
          <div
            key={rowId}
            onMouseDown={
              obj
                ? (e) => {
                    e.stopPropagation();
                    openLine(
                      rowId,
                      obj,
                      raw,
                      e.currentTarget.getBoundingClientRect()
                    );
                  }
                : undefined
            }
            className={`flex gap-2.5 px-3 leading-[19px] hover:bg-white/[0.035] ${
              obj ? "cursor-pointer" : ""
            }`}
          >
            {showTs && (
              <span className="flex-shrink-0 select-none pt-px font-mono text-[10.5px] leading-[19px] text-[#4E5666]">
                {fmtTime(entry.timestamp)}
              </span>
            )}
            <span
              className={`font-mono text-[12px] leading-[19px] ${
                wrap ? "whitespace-pre-wrap break-all" : "whitespace-pre"
              }`}
              style={{ color }}
            >
              {highlight(shown, query.trim())}
            </span>
            {decode && obj?.t === "tx" && obj.pkt === "0x11" && (
              <SnippetSparkline samples={obj.samples_mg} />
            )}
            {decode && obj?.t === "impact" && (
              <ImpactMeter peakMg={obj.peak_mg} />
            )}
            {decode && obj?.t === "tx" && obj.pkt === "0x10" && (
              <AxisBars comps={[obj.x_mg, obj.y_mg, obj.z_mg]} />
            )}
          </div>
        );
      }),
    [displayData, decode, wrap, showTs, query, openLine]
  );

  // ── Toolbar (full controls only when open) ──
  const toolbar = (
    <div
      className="flex h-11 items-center gap-2 border-b border-[#20242F] bg-[#12141C] px-3"
      style={{ colorScheme: "dark" }}
    >
      {/* Label + count */}
      <div className="flex flex-shrink-0 items-center gap-2">
        <TerminalSquare size={14} className="text-[#6B7280]" />
        <span className="text-[12px] font-semibold text-[#D1D5DB]">
          {t("devices.terminal.serialOutput")}
        </span>
        <span className="rounded-md bg-[#1D2130] px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-[#8B93A7]">
          {matchCount != null ? `${matchCount} / ${total}` : total}
        </span>
      </div>

      {open && (
        <>
          {/* Search — capped so it doesn't swallow the (very wide) bar */}
          <div className="flex min-w-0 max-w-[400px] flex-1 items-center gap-1.5 rounded-lg border border-[#252A36] bg-[#0C0E14] px-2.5 py-1.5 transition-colors focus-within:border-[#E07A3A]/70">
            <Search size={13} className="flex-shrink-0 text-[#6B7280]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onClick={stop}
              placeholder={t("devices.terminal.filterPlaceholder")}
              spellCheck={false}
              style={{ background: "transparent", color: "#E5E7EB" }}
              className="min-w-0 flex-1 font-mono text-[11px] placeholder-[#6B7280] outline-none"
            />
            {query && (
              <button
                onClick={(e) => {
                  stop(e);
                  setQuery("");
                }}
                title={t("devices.terminal.clearFilter")}
                className="flex-shrink-0 text-[#6B7280] hover:text-[#E5E7EB]"
              >
                <X size={13} />
              </button>
            )}
          </div>

          <div className="flex-1" />

          {/* View toggles — grouped */}
          <div className="flex items-center gap-0.5 rounded-lg bg-[#0C0E14] p-0.5">
            <IconBtn
              active={wrap}
              title={
                wrap
                  ? t("devices.terminal.wrapOn")
                  : t("devices.terminal.wrapOff")
              }
              onClick={(e) => {
                stop(e);
                setWrap((v) => !v);
              }}
            >
              <WrapText size={13} />
            </IconBtn>
            <IconBtn
              active={showTs}
              title={
                showTs
                  ? t("devices.terminal.timestampsOn")
                  : t("devices.terminal.timestampsOff")
              }
              onClick={(e) => {
                stop(e);
                setShowTs((v) => !v);
              }}
            >
              <Clock size={13} />
            </IconBtn>
            <IconBtn
              active={decode}
              title={
                decode
                  ? t("devices.terminal.decoding")
                  : t("devices.terminal.showingRaw")
              }
              onClick={(e) => {
                stop(e);
                setDecode((v) => !v);
              }}
            >
              <Braces size={13} />
            </IconBtn>
          </div>

          {/* Actions */}
          <IconBtn
            title={
              copied
                ? t("common.status.copied")
                : t("devices.terminal.copyVisible")
            }
            onClick={handleCopy}
          >
            {copied ? (
              <Check size={13} className="text-[#4ADE80]" />
            ) : (
              <Copy size={13} />
            )}
          </IconBtn>
          {total > 0 && (
            <IconBtn
              title={t("devices.terminal.clearTerminal")}
              onClick={handleClear}
              danger
            >
              <Trash2 size={13} />
            </IconBtn>
          )}
        </>
      )}

      {!open && <div className="flex-1" />}

      <span className="h-5 w-px flex-shrink-0 bg-[#20242F]" />

      {/* Live / idle pill */}
      <span
        className="flex flex-shrink-0 items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
        style={{
          borderColor: connected ? "rgba(34,197,94,0.35)" : "#2A2F3D",
          color: connected ? "#4ADE80" : "#6B7280",
        }}
      >
        <span className="relative flex h-1.5 w-1.5">
          {connected && (
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#16A34A] opacity-75" />
          )}
          <span
            className={`relative inline-flex h-1.5 w-1.5 rounded-full ${
              connected ? "bg-[#22C55E]" : "bg-[#6B7280]"
            }`}
          />
        </span>
        {connected ? t("common.status.live") : t("devices.terminal.idle")}
      </span>

      {/* Expand / collapse */}
      {docked ? (
        onToggleCollapse && (
          <IconBtn
            title={
              collapsed
                ? t("devices.terminal.expandSerial")
                : t("devices.terminal.collapseSerial")
            }
            onClick={(e) => {
              stop(e);
              onToggleCollapse();
            }}
          >
            <ChevronUp
              size={14}
              className={`transition-transform duration-150 ${
                collapsed ? "" : "rotate-180"
              }`}
            />
          </IconBtn>
        )
      ) : (
        <IconBtn
          title={
            isExpanded
              ? t("devices.terminal.collapse")
              : t("devices.terminal.expand")
          }
          onClick={(e) => {
            stop(e);
            setIsExpanded((v) => !v);
          }}
        >
          <ChevronUp
            size={14}
            className={`transition-transform duration-200 ${
              isExpanded ? "" : "rotate-180"
            }`}
          />
        </IconBtn>
      )}
    </div>
  );

  // ── Log body ──
  const body = (style) => (
    <div className="relative" style={{ colorScheme: "dark" }}>
      <div
        ref={scrollRef}
        onScroll={onScroll}
        className={`serial-terminal-scroll bg-[#0C0E14] py-1.5 ${
          wrap ? "overflow-y-auto" : "overflow-auto"
        }`}
        style={style}
      >
        {displayData.length === 0 ? (
          <div className="flex h-full min-h-[80px] flex-col items-center justify-center gap-1 py-6 text-center">
            <TerminalSquare size={18} className="text-[#374151]" />
            <p className="font-mono text-[11px] text-[#5B6472]">
              {total === 0
                ? t("devices.terminal.waitingForData")
                : t("devices.terminal.noMatches")}
            </p>
          </div>
        ) : (
          <>
            {isTruncated && (
              <p className="mb-1 px-3 text-center text-[10px] text-[#5B6472]">
                {t(
                  q
                    ? "devices.terminal.showingLastMatches"
                    : "devices.terminal.showingLast",
                  {
                    cap: DISPLAY_CAP.toLocaleString(),
                    total: matches.length.toLocaleString(),
                  }
                )}
              </p>
            )}
            {renderedRows}
          </>
        )}
      </div>

      {/* Jump-to-latest pill when scrolled up */}
      {!atBottom && (
        <button
          onClick={scrollToBottom}
          className="absolute bottom-2.5 left-1/2 flex -translate-x-1/2 items-center gap-1.5 rounded-full bg-[#E07A3A] px-3 py-1 text-[11px] font-semibold text-white shadow-lg transition hover:bg-[#D66B2E]"
        >
          <ArrowDownToLine size={12} />
          {unseen > 0
            ? t("devices.terminal.newLines", { count: unseen })
            : t("devices.terminal.jumpToLatest")}
        </button>
      )}
    </div>
  );

  const styleTag = (
    <style>{`
      .serial-terminal-scroll::-webkit-scrollbar { width: 8px; height: 8px; }
      .serial-terminal-scroll::-webkit-scrollbar-track { background: transparent; }
      .serial-terminal-scroll::-webkit-scrollbar-thumb { background: #374151; border-radius: 4px; }
      .serial-terminal-scroll::-webkit-scrollbar-thumb:hover { background: #4B5563; }
    `}</style>
  );

  // Pinned detail card, portalled to <body> so the scroll container can't clip
  // it. Its own mousedown stops propagation so clicking inside (copy, the
  // interactive graph) doesn't dismiss it.
  const detailPortal =
    selected && typeof document !== "undefined"
      ? createPortal(
          (() => {
            const { rect } = selected;
            const W = 330;
            const left = Math.max(
              8,
              Math.min(rect.left, window.innerWidth - W - 12)
            );
            const above = rect.top > window.innerHeight * 0.45;
            return (
              <div
                onMouseDown={(e) => e.stopPropagation()}
                className="fixed z-[9999]"
                style={{
                  left,
                  ...(above
                    ? { bottom: window.innerHeight - rect.top + 8 }
                    : { top: rect.bottom + 8 }),
                }}
              >
                <EventTooltip obj={selected.obj} raw={selected.raw} />
              </div>
            );
          })(),
          document.body
        )
      : null;

  // ── Docked: full-width bar, height controlled by the parent ──
  if (docked) {
    return (
      <div className={className}>
        {toolbar}
        {terminalHeight > 0 && body({ height: terminalHeight })}
        {styleTag}
        {detailPortal}
      </div>
    );
  }

  // ── Inline: collapsible card ──
  return (
    <div className={`overflow-hidden rounded-lg ${className}`}>
      {toolbar}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            {body({ maxHeight: 260 })}
          </motion.div>
        )}
      </AnimatePresence>
      {styleTag}
      {detailPortal}
    </div>
  );
};

export default SerialTerminal;
